import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { GiftIcon } from '@heroicons/react/24/outline';
import MobileLayout from '../components/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { clearPendingPlanInvite, savePendingPlanInvite } from '../features/invites/pendingInvite';

interface PlanInvitePreview {
  plan: string;
  planName: string;
  monthlyWords: number;
  email: string;
  emailMatches: boolean;
  subscribed: boolean;
}

/** Where an emailed invite to a complimentary plan lands: preview it, then accept. */
const AcceptPlanInvite: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [preview, setPreview] = useState<PlanInvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      savePendingPlanInvite(token);
      return;
    }
    // Signed in and on the invite: the remembered link has done its job.
    clearPendingPlanInvite();
    let cancelled = false;
    axios
      .get(`/plan-invites/${encodeURIComponent(token)}`)
      .then((res) => !cancelled && setPreview(res.data))
      .catch(
        (err) =>
          !cancelled &&
          setError(
            err?.response?.data?.message ||
              t('planInvite.invalid', 'This invite link is not valid.'),
          ),
      );
    return () => {
      cancelled = true;
    };
  }, [isLoading, user, token, t]);

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await axios.post(`/plan-invites/${encodeURIComponent(token)}/accept`);
      toast.success(
        t('planInvite.accepted', 'You are on {{plan}}. Your AI words are ready.', {
          plan: res.data.planName,
        }),
      );
      navigate('/stories', { replace: true });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t('planInvite.acceptError', 'Could not accept the invite. Try again.'),
      );
    } finally {
      setAccepting(false);
    }
  };

  const primary = { color: 'var(--ds-text-primary)' };
  const secondary = { color: 'var(--ds-text-secondary)' };
  const card = (children: React.ReactNode) => (
    <MobileLayout showNav={!!user} showHeader={!!user}>
      <div className="flex-1 flex items-center justify-center p-6">
        <div
          className="ds-card w-full max-w-md rounded-2xl p-6 space-y-4 text-center"
          style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
        >
          <GiftIcon className="w-10 h-10 mx-auto text-primary-400" />
          {children}
        </div>
      </div>
    </MobileLayout>
  );
  const spinner = (
    <div
      className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
      style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
    />
  );
  const toStories = (
    <button
      onClick={() => navigate('/stories')}
      className="w-full py-3 rounded-xl font-semibold border"
      style={{ borderColor: 'var(--ds-border)', ...primary }}
    >
      {t('planInvite.toStories', 'Go to my stories')}
    </button>
  );

  if (isLoading) return card(spinner);

  if (!user) {
    return card(
      <>
        <h1 className="text-xl font-semibold" style={primary}>
          {t('planInvite.signedOutTitle', "You've been invited to Plotweave")}
        </h1>
        <p className="text-sm" style={secondary}>
          {t(
            'planInvite.signedOutBody',
            'Sign in, or create a free account with the email address the invite was sent to. You will come straight back here.',
          )}
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold"
          >
            {t('planInvite.signUp', 'Create an account')}
          </button>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 rounded-xl font-semibold border"
            style={{ borderColor: 'var(--ds-border)', ...primary }}
          >
            {t('planInvite.signIn', 'Sign in')}
          </button>
        </div>
      </>,
    );
  }

  if (error) {
    return card(
      <>
        <p className="text-sm" style={primary}>
          {error}
        </p>
        {toStories}
      </>,
    );
  }

  if (!preview) return card(spinner);

  return card(
    <>
      <h1 className="text-xl font-semibold" style={primary}>
        {t('planInvite.title', 'Plotweave {{plan}}, on us', { plan: preview.planName })}
      </h1>
      <p className="text-sm" style={secondary}>
        {t(
          'planInvite.body',
          "You've been given a free {{plan}} plan: {{words}} AI words every month, no card needed.",
          { plan: preview.planName, words: preview.monthlyWords.toLocaleString() },
        )}
      </p>
      {!preview.emailMatches ? (
        <p className="text-sm" style={primary}>
          {t(
            'planInvite.wrongAccount',
            'This invite was sent to {{email}}. Sign in with that address (and verify it) to accept.',
            { email: preview.email },
          )}
        </p>
      ) : preview.subscribed ? (
        <p className="text-sm" style={primary}>
          {t(
            'planInvite.subscribed',
            'You already pay for a subscription. Cancel it in Manage billing; once your paid period has ended, open this link again or ask us for a new one.',
          )}
        </p>
      ) : (
        <button
          onClick={accept}
          disabled={accepting}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-semibold"
        >
          {t('planInvite.accept', 'Accept the invite')}
        </button>
      )}
      {toStories}
    </>,
  );
};

export default AcceptPlanInvite;
